import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { queryClient } from "@/queryClient";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { useState } from "react";
import { transferOwnership } from "@/middleware/members";

type Props = {
  open: boolean;
  onOpenClose: (open: boolean) => void;
  username: string;
};

export default function PromoteToOwnerDialog({
  open,
  onOpenClose,
  username,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const activeChatId = useNavigationState((state) => state.activeChatId);

  const handleTransferOwnership = async () => {
    if (!activeChatId) {
      return;
    }

    try {
      setIsLoading(true);
      await transferOwnership(username, activeChatId);

      toast({
        duration: 1500,
        description: t("transferOwnership.success"),
        variant: "default",
      });

      queryClient.invalidateQueries({
        queryKey: ["chat-members", activeChatId],
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      onOpenClose(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isLoading) {
          return;
        }
        onOpenClose(isOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("areYouSure.absolutelySure")}</DialogTitle>
          <DialogDescription>
            {t("transferOwnership.description", {
              user: username,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={() => onOpenClose(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="default"
            disabled={isLoading}
            onClick={() => handleTransferOwnership()}
          >
            {isLoading
              ? t("transferOwnership.promoting")
              : t("transferOwnership.promote")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
